import { useAuth } from "@clerk/clerk-react";
import { getMarkRange } from "@tiptap/core";
import { BubbleMenu, Editor as TipTapEditor } from "@tiptap/react";
import { OrganizationUserContext } from "contexts/Organization";
import { useContext, useEffect, useReducer } from "react";

import { OrganizationTemplateRubricSuggestion } from "features/Influencer/ContactList/schema";
import { fetcherAuth } from "utils/api";
import { trackEvent } from "utils/tracking";

import HyperlinkSuggestionPopup from "./Hyperlink";
import LinkAccuracySuggestionPopup from "./LinkAccuracy";
import MergeTagSuggestionPopup from "./MergeTag";
import OverusedPhrasesSuggestionPopup from "./OverusedPhrases";
import SentenceEditSuggestionPopup from "./SentenceEdit";
import SocialProofSuggestionPopup from "./SocialProof";
import { OrganizationTemplateRubricSuggestionType } from "./schema";

const SENTENCE_EDIT_TYPES = new Set<string>([
  OrganizationTemplateRubricSuggestionType.call_to_action,
  OrganizationTemplateRubricSuggestionType.opening_hook,
]);

export interface SuggestionResolution {
  suggestion: OrganizationTemplateRubricSuggestion;
  action: "applied" | "dismissed";
  beforeHtml: string;
}

interface SuggestionPopUpProps {
  editor: TipTapEditor | null;
  suggestions: OrganizationTemplateRubricSuggestion[];
  onSuggestionResolved?: (resolution: SuggestionResolution) => void;
}

export default function SuggestionPopUp({
  editor,
  suggestions,
  onSuggestionResolved,
}: SuggestionPopUpProps) {
  const { getToken } = useAuth();
  const { currentOrg } = useContext(OrganizationUserContext);
  const [, forceUpdate] = useReducer((x) => x + 1, 0);

  useEffect(() => {
    if (!editor) return;
    editor.on("selectionUpdate", forceUpdate);
    editor.on("update", forceUpdate);
    return () => {
      editor.off("selectionUpdate", forceUpdate);
      editor.off("update", forceUpdate);
    };
  }, [editor]);

  if (!editor) return null;

  const activeSuggestion = getActiveSuggestion(editor, suggestions);

  const handleApplied = (suggestionId: number, beforeHtml: string): void => {
    const suggestion = suggestions.find((s) => s.id === suggestionId);
    if (suggestion) {
      onSuggestionResolved?.({ suggestion, action: "applied", beforeHtml });
    }
  };

  const handleDismiss = async (
    suggestion: OrganizationTemplateRubricSuggestion,
  ): Promise<void> => {
    if (!currentOrg?.id) return;

    const beforeHtml = editor.getHTML();

    const highlightMark = editor.state.schema.marks.highlight;
    if (highlightMark) {
      const { from } = editor.state.selection;
      const $pos = editor.state.doc.resolve(from);
      const range = getMarkRange($pos, highlightMark);
      if (range) {
        editor
          .chain()
          .focus()
          .setTextSelection({ from: range.from, to: range.to })
          .unsetMark("highlight")
          .run();
      }
    }

    await fetcherAuth(
      getToken,
      `/api/organization/${currentOrg.id}/organization-template-rubric-suggestions/suggestion/${suggestion.id}`,
      "PATCH",
      {},
      { status: "dismissed" },
    );

    onSuggestionResolved?.({ suggestion, action: "dismissed", beforeHtml });
  };

  return (
    <BubbleMenu
      editor={editor}
      tippyOptions={{ duration: 50, zIndex: 1201, placement: "bottom-start" }}
      shouldShow={({ editor: e }) => {
        trackEvent("Template - Suggestion Popup Viewed", {
          suggestionType: activeSuggestion?.type,
        });
        return e.isActive("highlight");
      }}
    >
      {activeSuggestion?.type ===
        OrganizationTemplateRubricSuggestionType.hyperlink && (
        <HyperlinkSuggestionPopup
          editor={editor}
          suggestion={activeSuggestion}
          onApplied={handleApplied}
          onDismiss={() => handleDismiss(activeSuggestion)}
        />
      )}
      {activeSuggestion?.type ===
        OrganizationTemplateRubricSuggestionType.overused_phrases && (
        <OverusedPhrasesSuggestionPopup
          editor={editor}
          suggestion={activeSuggestion}
          onApplied={handleApplied}
          onDismiss={() => handleDismiss(activeSuggestion)}
        />
      )}
      {activeSuggestion?.type ===
        OrganizationTemplateRubricSuggestionType.social_proof && (
        <SocialProofSuggestionPopup
          editor={editor}
          suggestion={activeSuggestion}
          onApplied={handleApplied}
          onDismiss={() => handleDismiss(activeSuggestion)}
        />
      )}
      {activeSuggestion?.type ===
        OrganizationTemplateRubricSuggestionType.merge_tags && (
        <MergeTagSuggestionPopup
          editor={editor}
          suggestion={activeSuggestion}
          onApplied={handleApplied}
          onDismiss={() => handleDismiss(activeSuggestion)}
        />
      )}
      {activeSuggestion?.type ===
        OrganizationTemplateRubricSuggestionType.link_accuracy && (
        <LinkAccuracySuggestionPopup
          editor={editor}
          suggestion={activeSuggestion}
          onApplied={handleApplied}
          onDismiss={() => handleDismiss(activeSuggestion)}
        />
      )}
      {activeSuggestion && SENTENCE_EDIT_TYPES.has(activeSuggestion.type) && (
        <SentenceEditSuggestionPopup
          editor={editor}
          suggestion={activeSuggestion}
          onApplied={handleApplied}
          onDismiss={() => handleDismiss(activeSuggestion)}
        />
      )}
    </BubbleMenu>
  );
}

function getActiveSuggestion(
  editor: TipTapEditor,
  suggestions: OrganizationTemplateRubricSuggestion[],
): OrganizationTemplateRubricSuggestion | undefined {
  const highlightMark = editor.state.schema.marks.highlight;
  if (!highlightMark) return undefined;
  const { from } = editor.state.selection;
  const $pos = editor.state.doc.resolve(from);
  const range = getMarkRange($pos, highlightMark);
  if (!range) return undefined;
  const text = normalizeAnchor(
    editor.state.doc.textBetween(range.from, range.to),
  );
  return suggestions.find(
    (s) =>
      s.status === "pending" &&
      normalizeAnchor(stripHtml(s.anchorText)) === text,
  );
}

function stripHtml(value: string): string {
  return (
    new DOMParser().parseFromString(value, "text/html").body.textContent ?? ""
  );
}

function normalizeAnchor(value: string): string {
  return value.replace(/\s+/g, " ").trim();
}
